// ── ApiError class ────────────────────────────────────────────────────

export class ApiError extends Error {
    statusCode: number
    code: string
    requestId: string | null
    /**
     * Payload structuré joint à l'erreur via le kwarg `extra=` côté backend
     * (`crm.helpers.rfc7807_error`). Permet aux appelants de récupérer des
     * données métier (ex. `existing_client_id` sur SIRET_DUPLICATE) sans
     * reparser la réponse, et donc d'afficher un dialogue dédié plutôt qu'un
     * toast technique.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    details: Record<string, any>
    /**
     * `message` est une phrase COMPOSÉE par l'écran, dans sa langue (une table de module, la table
     * transverse) — pas celle du serveur. `toastApiError` la dit telle quelle, sauf une panne : la
     * relire par son CODE la remplacerait par la phrase générique de la famille (`PARENT_NOT_FOUND`
     * → « élément introuvable »). Posé par le lecteur qui compose (`erreurLue`,
     * `refusDeCommissionTraduit`) ; `declare` : aucune propriété sur les autres erreurs.
     */
    declare phraseDEcran?: boolean

    constructor(
        message: string,
        statusCode: number,
        code = 'UNKNOWN',
        requestId: string | null = null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        details: Record<string, any> = {},
    ) {
        super(message)
        this.name = 'ApiError'
        this.statusCode = statusCode
        this.code = code
        this.requestId = requestId
        this.details = details
    }
}

// ── Le code support d'une erreur ──────────────────────────────────────
//
// 🔴 **La règle vit ICI, pas dans `toast-error.ts`.** Elle y était, et le
// toast n'est pas la seule surface qui affiche un refus d'API : le dépôt
// d'une pièce justificative garde le sien À L'ÉCRAN, sous la zone de dépôt.
// Cette surface-là n'avait aucun accès à la règle et JETAIT le `request_id`.
// L'importer depuis `toast-error` était le réflexe — mais ce module tire
// `sonner` ET `@/i18n`, qui s'initialise au chargement : un hook qui le lit
// faisait tomber des cahiers voisins AVANT leur premier test. Le code support
// est une propriété de l'ERREUR, pas de sa présentation : sa place est ici,
// où rien n'est importé.

/**
 * Codes d'erreur métier connus (validation utilisateur, conflit anticipé,
 * ressource manquante, etc.) — ne JAMAIS afficher la mention "transmettez ce
 * code au support" pour ces cas, c'est une erreur de saisie pas une panne.
 *
 * Liste sémantique : tout code en SCREAMING_SNAKE_CASE qui correspond à une
 * règle métier connue est considéré "anticipé" et présenté sobrement.
 *
 * Heuristique : un code différent de `UNKNOWN` ET de statut HTTP < 500 est
 * presque toujours métier. On garde la mention support uniquement pour les
 * 5xx (vraies pannes) ou les codes manquants — c'est là que l'utilisateur
 * a effectivement besoin de transmettre le request_id.
 */
function _isAnticipatedBusinessError(err: ApiError): boolean {
    if (err.statusCode >= 500) return false
    if (!err.code || err.code === 'UNKNOWN') return false
    return true
}

/**
 * Le CODE SUPPORT d'une erreur, ou `null` quand elle n'en mérite pas.
 *
 * 🔴 **Exporté parce que le toast n'est pas la seule surface qui affiche une
 * erreur d'API.** Le dépôt d'une pièce justificative, par exemple, garde son
 * refus À L'ÉCRAN sous la zone de dépôt (`useChecklistProofUpload`) : sans un
 * accès à cette règle, cette surface-là ré-inventait la décision — et, dans les
 * faits, JETAIT le `request_id`. Une seconde table de décision aurait divergé au
 * premier changement ; la règle est écrite ici, une fois.
 *
 * ⚠️ Rend `null` pour un refus métier anticipé : afficher « transmettez ce code
 * au support » à quelqu'un qui a coché une case de trop, c'est lui dire que le
 * produit est en panne alors que c'est LUI qu'on refuse.
 */
export function supportCodeOf(err: unknown): string | null {
    if (!(err instanceof ApiError)) return null
    if (_isAnticipatedBusinessError(err)) return null
    return err.requestId
}

// ── Structured extraction ─────────────────────────────────────────────

/**
 * Extract structured error information from an API response body.
 *
 * Handles all backend error formats:
 *   { detail: { code, message|detail|title, request_id } }   ← RFC 7807 / HTTPException
 *   { detail: "string" }                                     ← simple detail
 *   { detail: [...], request_id }                             ← Pydantic 422 + request_id at root
 *   { code, message, request_id }                             ← flat
 */
export function extractStructuredApiError(body: unknown): {
    message: string
    code: string
    requestId: string | null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    details: Record<string, any>
} {
    const fallback = { message: '', code: 'UNKNOWN', requestId: null, details: {} }
    if (body == null || typeof body !== 'object') return fallback

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const obj = body as Record<string, any>
    const detail = obj.detail

    // Pydantic validation errors: { detail: [{ msg, loc, type, ctx }], request_id? }
    // ⚠️ Les entrées restent dans `details.validation_errors` : la phrase jointe
    // est ANGLAISE et ne dit qu'une erreur lisible sur plusieurs. `type` et `ctx`
    // sont ce qui permet de les dire TOUTES dans la langue de l'écran
    // (`api-error-transverse.ts`, décision PO du 2026-09-24).
    if (Array.isArray(detail)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const message = detail.map((d: any) => d.msg || d.message || String(d)).join('. ')
        const details = { validation_errors: detail }
        return { message, code: 'VALIDATION_ERROR', requestId: obj.request_id ?? null, details }
    }

    // Nested detail object: { detail: { code, message|detail|title, request_id, ...extra } }
    if (typeof detail === 'object' && detail !== null) {
        const message = detail.message || detail.detail || detail.title || ''
        const code = detail.code || 'UNKNOWN'
        const requestId = detail.request_id ?? obj.request_id ?? null
        // Conserve toutes les clés "extra" écrites à plat dans `detail` par
        // `rfc7807_error(extra=…)` (ex. existing_client_id, blocking, etc.).
        const { code: _c, message: _m, detail: _d, title: _t, request_id: _r, ...extra } = detail
        return { message, code, requestId, details: extra }
    }

    // Simple string detail: { detail: "string" }
    if (typeof detail === 'string') {
        return { message: detail, code: obj.code || 'UNKNOWN', requestId: obj.request_id ?? null, details: {} }
    }

    // Flat format: { code, message, request_id, ...extra }
    if (typeof obj.message === 'string') {
        const { code: _c, message: _m, request_id: _r, detail: _d, ...extra } = obj
        return { message: obj.message, code: obj.code || 'UNKNOWN', requestId: obj.request_id ?? null, details: extra }
    }

    return fallback
}

// ── Centralized error handler ─────────────────────────────────────────

/**
 * Parse a non-ok HTTP response and throw an ApiError with structured fields.
 * Drop-in replacement for all module-specific handleError functions.
 */
export async function handleApiError(response: Response): Promise<never> {
    const body = await response.json().catch(() => ({}))
    const { message, code, requestId, details } = extractStructuredApiError(body)
    throw new ApiError(
        message || `API error ${response.status}`,
        response.status,
        code,
        requestId,
        details,
    )
}

// ── Legacy helper (unchanged) ─────────────────────────────────────────

/**
 * Extract a human-readable error message from an API response error.
 * Handles the various error response formats used by the backend.
 */
export function extractApiError(error: unknown): string {
  if (error instanceof Error) {
    // Certains helpers (ex. satisfaction `asJson`) lèvent `new Error('request_failed')`
    // en attachant le corps RFC-7807 structuré sur `.detail` — on le lit en priorité,
    // sinon le toast n'afficherait que « request_failed ».
    const attached = (error as { detail?: unknown }).detail
    if (attached != null) {
      const fromDetail = _extractFromObject({ detail: attached })
      if (fromDetail) return fromDetail
    }
    // Try to parse the message as JSON (fetch errors often stringify the response)
    try {
      const parsed = JSON.parse(error.message)
      return _extractFromObject(parsed) || error.message
    } catch {
      return error.message
    }
  }
  if (typeof error === 'object' && error !== null) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return _extractFromObject(error as Record<string, any>) || JSON.stringify(error)
  }
  if (error == null) return ''
  return String(error)
}

/**
 * Drills into common API error shapes:
 *   { detail: "string" }
 *   { detail: { code, message } }
 *   { detail: [{ msg, loc, type }] }   ← Pydantic validation
 *   { message: "string" }
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function _extractFromObject(obj: Record<string, any>): string {
  const detail = obj.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    // Pydantic validation errors
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return detail.map((d: any) => d.msg || d.message || String(d)).join('. ')
  }
  if (typeof detail === 'object' && detail !== null) {
    return detail.message || detail.title || JSON.stringify(detail)
  }
  if (typeof obj.message === 'string') return obj.message
  return ''
}
